// ===========================================
// Late Fees card: what is wrong with the Last Chance step (#372 slice C, plan C2.2)
// ===========================================
// A late fee is only charged after a Last Chance email has reached the family, and that email must
// tell them the fee and the date. The server judges this (`lastChanceWarning`, three reasons, only
// while fees are effectively on); the first-fee gap is a soft check from the reminder steps. The
// server stays the authority on every refusal; these only say what to fix, in words.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Stack } from '@mui/material';
import { adminSettingsKeys, useReminderSteps } from '../../../hooks/useAdminSettings';
import { usePermissions } from '../../../hooks/usePermissions';
import { getEmailTemplates } from '../../../api/emailBranding.api';
import type { LastChanceWarning } from '../../../types/adminSettings.types';
import { LATE_FEE_PREVIEW_PREFIX } from '../../../hooks/useLateFeePreview';
import TemplateEditorDialog from './TemplateEditorDialog';

/** Fewer days than this between the Last Chance and the first fee leaves families little time to pay. */
export const MIN_LAST_CHANCE_GAP_DAYS = 2;

export const LAST_CHANCE_WARNING_COPY: Record<LastChanceWarning, string> = {
  BODY_MISSING:
    "The Last Chance email doesn't tell the family the late fee or the date it is added. Add the late fee sentence to its wording before fees start.",
  STEP_DISABLED:
    'The Last Chance reminder step is off or has been deleted, so no late fee can be charged. Turn it back on (or add one) in Payment Reminders.',
  STEP_TOO_LATE:
    'The Last Chance email goes out on or after the first fee day, so families would be charged before they are told. Move it earlier in Payment Reminders, or move the first fee later.',
};

interface Props {
  warning: LastChanceWarning | null | undefined;
  /** Are late fees on in the form right now? Only then is the gap worth a word. */
  feesOn: boolean;
  /** The first-fee day typed in the form ('' while it is being edited). */
  firstDays: number | '';
}

export default function LastChanceNotices({ warning, feesOn, firstDays }: Props) {
  const { can } = usePermissions();
  const canEditWording =
    can('operations_invoices', 'write') && can('system_settings', 'write') && can('communications_settings', 'write');
  const queryClient = useQueryClient();
  const steps = useReminderSteps();
  const lastChance = steps.data?.steps.find((s) => s.kind === 'LAST_CHANCE' && s.enabled);
  const [wordingOpen, setWordingOpen] = useState(false);

  // The same list System Emails reads (shared cache); fetched only when the editor is asked for.
  const templates = useQuery({ queryKey: ['emailTemplates'], queryFn: getEmailTemplates, enabled: wordingOpen });
  const wordingKey = wordingOpen && lastChance ? `step:${lastChance.id}` : null;
  const wordingTemplate =
    wordingKey && Array.isArray(templates.data) ? templates.data.find((t) => t.key === wordingKey) : undefined;

  const first = Number(firstDays);
  const gap = lastChance && firstDays !== '' && Number.isFinite(first) ? first - lastChance.dayOffset : null;
  const gapTight = feesOn && gap !== null && gap < MIN_LAST_CHANCE_GAP_DAYS && warning !== 'STEP_TOO_LATE';

  if (!warning && !gapTight) return null;

  return (
    <Stack spacing={1.5}>
      {warning && (
        <Alert
          severity="warning"
          action={
            warning === 'BODY_MISSING' && canEditWording && lastChance ? (
              <Button color="inherit" size="small" onClick={() => setWordingOpen(true)}>
                Edit the wording
              </Button>
            ) : undefined
          }
        >
          {LAST_CHANCE_WARNING_COPY[warning]}
        </Alert>
      )}
      {gapTight && (
        <Alert severity={gap! < 1 ? 'warning' : 'info'}>
          {gap! < 1
            ? 'The first late fee comes on or before the day the Last Chance email goes out.'
            : 'The first late fee comes only a day after the Last Chance email.'}{' '}
          That leaves families little time to pay. Two days or more is kinder.
        </Alert>
      )}
      {wordingOpen && templates.isSuccess && !wordingTemplate && (
        <Alert severity="warning" onClose={() => setWordingOpen(false)}>
          The Last Chance wording isn&apos;t available here. Open Payment Reminders above and choose Edit wording.
        </Alert>
      )}
      <TemplateEditorDialog
        open={wordingOpen && !!wordingTemplate}
        templateKey={wordingKey ?? ''}
        templateName={wordingTemplate?.name}
        onClose={() => setWordingOpen(false)}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: adminSettingsKeys.invoice() });
          queryClient.invalidateQueries({ queryKey: LATE_FEE_PREVIEW_PREFIX });
          queryClient.invalidateQueries({ queryKey: adminSettingsKeys.reminderSteps() });
          queryClient.invalidateQueries({ queryKey: ['emailTemplates'] });
          setWordingOpen(false);
        }}
      />
    </Stack>
  );
}
